import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { getPageShellDataSafely } from "@/services/contenful/getPageShellDataSafe";
import { InternalTitleToMatchForPDP } from "@/constants";
import { getProductDetailsFromServer } from "@/services/server/pdp.server";
import Head from "next/head";
import { usePathname, useSearchParams } from "next/navigation";

const Loader = dynamic(() => import("@/components/loader"));
const DetailsComponent = dynamic(() => import("@/components/detailsComponent"));

const LandingPage = ({
  pdpData = {},
  compareMappingsData = {},
  marketConfiguration = [],
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  if (router.isFallback) {
    return <Loader loading={router.isFallback} />;
  }

  const {
    ProductDescriptionProduct_s: pageTitle = "",
    ProductNarrativeDescription_s: productDescription = "",
  } = pdpData || {};

  return (
    <>
      <Head>
        <title>{`${pageTitle}`}</title>
        {productDescription && (
          <meta name="description" content={productDescription} />
        )}
      </Head>
      <DetailsComponent
        key={`${pathname}-${searchParams.toString()}`}
        productDetailsData={pdpData}
        skuInfo={router?.query?.sku || ""}
        compareMappingsData={compareMappingsData}
        marketConfiguration={marketConfiguration}
      />
    </>
  );
};

export async function getServerSideProps({ req, params, preview, query }) {
  const { slug = "/" } = params;
  const {
    headerNavigationData,
    footerNavigationData,
    promoBannerData,
    worldwideMenuData,
    compareMappingsData,
    apacConfigurationsData,
  } = await getPageShellDataSafely({
    slug: `/product-details/${slug}`,
    preview,
    contentType: "brazilLandingPage",
    fetchEntryBySlug: false,
  });

  const configData =
    apacConfigurationsData.items?.[0]?.fields?.configurations.filter(config =>
      config?.fields?.internalTitle
        ?.toLowerCase()
        ?.includes(InternalTitleToMatchForPDP)
    ) || {};

  const marketConfiguration = configData?.[0]?.fields?.config || [];
  const pdpSlug = slug;
  const { sku = "" } = query || {};
  const pdpData = await getProductDetailsFromServer(pdpSlug, req, sku);

  return {
    props: {
      headerNavigationData,
      footerNavigationData,
      promoBannerData,
      world: worldwideMenuData,
      pdpData,
      compareMappingsData,
      params,
      marketConfiguration,
    },
  };
}

LandingPage.propTypes = {
  pageData: PropTypes.object,
  locale: PropTypes.string,
  pdpData: PropTypes.object,
  compareMappingsData: PropTypes.object,
  facetLabelsState: PropTypes.object,
  categoryConfig: PropTypes.string,
  params: PropTypes.object,
};

export default LandingPage;
